import type { Booking } from '@/types/booking';
import React from 'react';
import { Link } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBookings } from '@/contexts/BookingContext';
import { getDuplicateVersion } from '@/lib/metadata-utils';

interface LinkedBookingIndicatorProps {
  booking: Booking;
  className?: string;
}

export function LinkedBookingIndicator({ booking, className }: LinkedBookingIndicatorProps) {
  const { getLinkedBookingsForId } = useBookings();
  const linkedBookings = getLinkedBookingsForId(booking.id);

  if (linkedBookings.length === 0) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={cn(
              "inline-flex items-center gap-1 cursor-help transition-colors",
              "hover:bg-purple-50 hover:border-purple-200",
              className
            )}
          >
            <Link className="h-3 w-3 text-purple-500" />
            <span className="text-purple-700">
              {linkedBookings.length} linked {linkedBookings.length === 1 ? 'copy' : 'copies'}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-2">
            <div className="font-medium text-sm">Linked Bookings:</div>
            <div className="space-y-1">
              {linkedBookings.map(linked => (
                <div key={linked.id} className="text-sm flex items-center gap-2">
                  <span className="text-purple-500">•</span>
                  <span>Copy #{getDuplicateVersion(linked)}</span>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}